import React, { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Link } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'
import Alert from 'react-bootstrap/Alert'
import Form from 'react-bootstrap/Form'

import { ButtonWithLoadingState } from '../Buttons'
import { useTranslation } from 'react-i18next'
import SafelySetInnerHTML from '../SafelySetInnerHTML'
import getThemeValue from '../../utils/getThemeValue'
import PasswordInputField from '../Forms/PasswordInputField'
import { inject } from 'mobx-react'

const StyledLoginBoxHeadline = styled.h1`
  font-size: 1.6em;
  font-weight: 600;
  text-align: center;
  color: white;

  @media screen and (min-width: 425px) {
    font-size: 1.8rem;
  }
  @media screen and (min-width: 576px) {
    font-size: 2.5rem;
  }
`

const StyledLoginBoxText = styled.h5`
  margin-bottom: 1rem;
  text-align: center;
  color: white;
`

const StyledLoginButtonWrap = styled.div`
  margin-top: 0.5rem;
`
const LoginBox = observer(({ loginStore }) => {
  const { t } = useTranslation()
  const theme = useTheme()

  // --- Handle unmount cleanup ---
  useEffect(() => {
    return () => loginStore.setLoginButtonState(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleOnSubmit = (e) => {
    e.preventDefault()
    loginStore.handleLoginSubmit()
  }

  const loginErrorMessage = getThemeValue('userLogin', 'loginErrorMessage', '')
  const loginBoxText = getThemeValue('userLogin', 'loginBoxText', '')

  return (
    <div className='styledBox smallBox'>
      <StyledLoginBoxHeadline>
        {getThemeValue('userLogin', 'loginBoxHeadline', '')}
      </StyledLoginBoxHeadline>

      {loginBoxText && (
        <>
          <StyledLoginBoxText>{loginBoxText}</StyledLoginBoxText>
          <hr />
        </>
      )}

      {loginStore.someLoginError && (
        <Alert variant='danger'>
          {loginErrorMessage ? (
            <SafelySetInnerHTML>{loginErrorMessage}</SafelySetInnerHTML>
          ) : (
            t('loginBox:checkLoginData')
          )}
        </Alert>
      )}

      {loginStore.specificLoginError && (
        <Alert variant='danger'>{loginStore.specificLoginError}</Alert>
      )}

      <Form onSubmit={handleOnSubmit}>
        {/* USERNAME */}
        <Form.Group controlId='formBasicEmail' className='mb-3'>
          <Form.Label>
            {getThemeValue('userLogin', 'usernameLabel', '')}
          </Form.Label>
          <Form.Control
            type={theme.userLogin.type}
            placeholder={getThemeValue('userLogin', 'usernamePlaceholder', '')}
            onChange={(e) => loginStore.setLoginDataUsername(e.target.value)}
            required
          />
          <div className='invalid-feedback'>{t('loginBox:checkInputData')}</div>
        </Form.Group>

        {/* PASSWORD */}
        <Form.Group controlId='formBasicPassword' className='mb-3'>
          <Form.Label>
            {getThemeValue('userLogin', 'passwordLabel', '')}
          </Form.Label>
          <PasswordInputField
            inputKey='formLoginBoxPassword'
            placeholder={getThemeValue('userLogin', 'passwordPlaceholder', '')}
            onChange={(e) => loginStore.setLoginDataPassword(e.target.value)}
            required
          >
            <div className='invalid-feedback'>
              {t('loginBox:checkPassword')}
            </div>
          </PasswordInputField>
        </Form.Group>

        {/* LINKS + BUTTON */}
        <div className='d-flex justify-content-between'>
          <Form.Text>
            <Link to='/password-reset'>{t('loginBox:lostPassword')}</Link>
          </Form.Text>

          <StyledLoginButtonWrap className='login-button'>
            <ButtonWithLoadingState
              buttonText={t('loginBox:loginButton')}
              isLoading={loginStore.isLoginButtonLoading}
              type='submit'
              variant='primary'
            />
          </StyledLoginButtonWrap>
        </div>

        {/* OPTIONAL REGISTRATION LINK */}
        {getThemeValue('userLogin', 'registrationButton', false) && (
          <Form.Text>
            <Link to='/sign-up'>{t('loginBox:registrationButton')}</Link>
          </Form.Text>
        )}
      </Form>
    </div>
  )
})

export default inject('loginStore')(LoginBox)
